import { FC, useMemo } from 'react';
import {
  checkIsRiyal,
  getRiyalFormattedPrice,
  shouldAddCurrencyInPrice
} from 'app/[lang]/[esite]/utils/paymentUtils';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { Conditional } from '@cat-ecom/pcc-components';
import { useTranslations } from 'next-intl';
import { RiyalPriceFontSize } from '@cat-ecom/pcc-components/src/constants/commonConstants';

type CustomStyles = {
  container?: string;
  price?: string;
  currency?: string;
};

interface Props {
  amount: string;
  currency: string;
  currencyLdFeatureFlag: boolean;
  showDiscountSection?: boolean;
  isRTL?: boolean;
  applyDefaultStyles?: boolean;
  customStyles?: CustomStyles;
  replaceCurrencyWithEaLabel?: boolean;
  sizeforRiyalFormattedPrice?: RiyalPriceFontSize;
  customTestIdPrefix?: string;
}

export const FormattedPriceWithCurrency: FC<Props> = ({
  amount = '',
  currency = '',
  currencyLdFeatureFlag = false,
  isRTL = false,
  customStyles = {},
  replaceCurrencyWithEaLabel = false,
  sizeforRiyalFormattedPrice = RiyalPriceFontSize.SM,
  customTestIdPrefix = 'formatted-price-with-currency'
}) => {
  const t = useTranslations();
  const getDefaultContainerStyles = useMemo(
    () => ({
      container:
        customStyles?.container && !isEmpty(customStyles.container)
          ? customStyles.container
          : 'd-flex flex-row align-items-center',
      price:
        customStyles?.price && !isEmpty(customStyles.price)
          ? customStyles.price
          : 'cat-u-theme-typography-title-sm px-1',
      currency:
        customStyles?.currency && !isEmpty(customStyles.currency)
          ? customStyles.currency
          : ''
    }),
    [customStyles]
  );
  const getTestId = useMemo(
    () =>
      !isEmpty(customTestIdPrefix)
        ? `${customTestIdPrefix}-container`
        : 'formatted-price-with-currency-container',
    [customTestIdPrefix]
  );
  const getFormattedPriceAndCurrency = (
    amountValue = '',
    currencyText = '',
    currencyLdFeatureFlagValue = false
  ) => {
    const showCurrencyText =
      shouldAddCurrencyInPrice(currencyText) && !replaceCurrencyWithEaLabel;
    const isRiyalCurrency =
      currencyLdFeatureFlagValue && checkIsRiyal(currencyText);
    const eaLabel = replaceCurrencyWithEaLabel ? t('ITEM_EA_ABB') : '';
    return (
      <div
        className={getDefaultContainerStyles.container}
        data-testid={getTestId}
      >
        <Conditional test={isRiyalCurrency}>
          {getRiyalFormattedPrice({
            price: `${amountValue} ${eaLabel}`,
            priceWrapperClasses: getDefaultContainerStyles?.price
              ? getDefaultContainerStyles?.price
              : 'cat-u-theme-typography-title-sm px-1',
            isRTL: isRTL,
            size: sizeforRiyalFormattedPrice
          })}
        </Conditional>
        <Conditional test={!isRiyalCurrency}>
          {
            <span
              className={getDefaultContainerStyles.price}
            >{`${amountValue} ${eaLabel}`}</span>
          }
        </Conditional>
        <Conditional test={showCurrencyText}>
          <span className={getDefaultContainerStyles.currency}>
            {currencyText}
          </span>
        </Conditional>
      </div>
    );
  };
  if (isEmpty(amount)) return '';
  return getFormattedPriceAndCurrency(amount, currency, currencyLdFeatureFlag);
};
